import { Dialog, DialogWindow } from '../../../../../../widgets/dialog';
import { Icon } from '../../../../../../widgets/icon';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import React, { FC, useEffect } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { stringFormat, T } from 'src/shared/helpers';
import { productLogicName } from 'src/shared/productNames';
import GroupIcon from 'src/shared/material_icons/action/join_inner.svg';
import StatesIcon from 'src/shared/material_icons/action/swap_horiz.svg';
import PartsIcon from 'src/shared/material_icons/maps/layers.svg';
import PresetIcon from 'src/shared/material_icons/action/view_quilt.svg';
import TextIcon from 'src/shared/material_icons/content/font_download.svg';
import ImportIcon from 'src/shared/material_icons/file/download.svg';
import SparklesIcon from 'src/shared/material_icons/image/auto_awesome.svg';
import ImageIcon from 'src/shared/material_icons/image/image.svg';
import PaletteIcon from 'src/shared/material_icons/image/palette.svg';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import CompoundIcon from 'src/shared/material_icons/communication/call_split.svg';
import StairsIcon from 'src/shared/material_icons/places/stairs.svg';
import styled from 'styled-components';
import type { composerAddGalleryDialogPrintTypesQuery } from './__generated__/composerAddGalleryDialogPrintTypesQuery.graphql';

export type ComposerUseCaseKey = 'color' | 'showhide' | 'engraving' | 'image' | 'state' | 'compound';
export type ComposerBuildCardKey = 'attribute' | 'personalisation' | 'group' | 'step' | 'import' | 'preset';

interface GalleryCard {
    key: ComposerUseCaseKey | ComposerBuildCardKey;
    name: string;
    description: string;
    icon: React.ReactNode;
}

interface BuildCard extends GalleryCard {
    key: ComposerBuildCardKey;
    /** La riga che sta sul bottone: la spiegazione intera resta nel tooltip. */
    hint: string;
    /** Il tasto che la crea senza mouse, finché la finestra è aperta. Solo per i quattro vuoti. */
    shortcut?: string;
}

const printTypesQuery = graphql`
    query composerAddGalleryDialogPrintTypesQuery {
        printTypes(first: 200) {
            edges {
                node {
                    printTypeID
                }
            }
        }
    }
`;

const GalleryWindow = styled(DialogWindow)`
    width: min(880px, calc(100vw - 40px));
    max-width: 880px;
    max-height: min(880px, calc(100vh - 48px));
    flex-basis: 880px;
    padding: 0;
    overflow: hidden;
    border-radius: 12px;
    box-shadow: 0 20px 52px rgb(30 42 56 / 24%);

    > h1 {
        display: flex;
        height: 44px;
        flex: 0 0 44px;
        align-items: center;
        margin: 0;
        padding: 0 16px;
        color: var(--composer-fg, #1e2a38);
        border-bottom: 1px solid var(--composer-line, #dce3eb);
        font-size: 13.5px;
    }
`;

const GalleryBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    overflow: auto;
    padding: 14px 16px 16px;
`;

const Lead = styled.p`
    margin: 0;
    color: var(--composer-muted, #5c6b7a);
    font-size: 12.5px;
    line-height: 1.5;
`;

const Group = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const GroupTitle = styled.h3`
    margin: 0;
    color: var(--composer-faint, #677078);
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

// DUE STRADE DELLO STESSO GESTO — partire da un modello pronto — e ognuna col suo colore. Scegliere
// produce un attributo con le sue opzioni, aggiungere produce una personalizzazione: il colore lo
// dice prima di leggere il titolo, e i due riquadri restano vicini perché il concetto è uno.
type TemplatePath = 'chooses' | 'adds';

const pathInk = (path: TemplatePath) => (path === 'chooses' ? 'var(--composer-kind-attribute, #0e7c7c)' : 'var(--composer-accent-deep, #c25309)');
const pathWash = (path: TemplatePath) => (path === 'chooses' ? 'rgb(14 124 124 / 6%)' : 'var(--composer-accent-wash, rgb(232 101 10 / 8%))');
const pathLine = (path: TemplatePath) => (path === 'chooses' ? 'rgb(14 124 124 / 28%)' : 'rgb(232 101 10 / 30%)');

const TemplatePaths = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 10px;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

const PathPanel = styled.div<{ $path: TemplatePath }>`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    background: ${(props) => pathWash(props.$path)};
    border: 1px solid ${(props) => pathLine(props.$path)};
    border-radius: 10px;
`;

const PathHeader = styled.div<{ $path: TemplatePath }>`
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 0 2px;

    strong {
        color: ${(props) => pathInk(props.$path)};
        font-size: 10.5px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    span {
        overflow: hidden;
        color: var(--composer-faint, #677078);
        font-size: 11px;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`;

const PathCards = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 560px) {
        grid-template-columns: 1fr;
    }
`;

const Card = styled.button<{ $path?: TemplatePath }>`
    display: flex;
    min-width: 0;
    align-items: flex-start;
    gap: 10px;
    padding: 10px;
    color: var(--composer-fg, #1e2a38);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 8px;
    cursor: pointer;
    text-align: left;

    &:hover,
    &:focus-visible {
        border-color: ${(props) => (props.$path ? pathInk(props.$path) : 'var(--composer-faint, #677078)')};
        outline: none;
    }
`;

const CardIcon = styled.span<{ $path?: TemplatePath }>`
    display: inline-flex;
    width: 30px;
    height: 30px;
    flex: 0 0 30px;
    align-items: center;
    justify-content: center;
    color: ${(props) => (props.$path ? pathInk(props.$path) : 'var(--composer-muted, #5c6b7a)')};
    background: ${(props) => (props.$path ? pathWash(props.$path) : 'var(--composer-sunken, #f4f7fa)')};
    border-radius: 6px;

    ${Icon} {
        width: 18px;
        height: 18px;
    }
`;

const CardText = styled.span`
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 12.5px;
        line-height: 16px;
    }

    span {
        color: var(--composer-faint, #677078);
        font-size: 11px;
        line-height: 1.4;
    }
`;

const AIBand = styled(Card)`
    width: 100%;
    align-items: center;
    padding: 10px 14px;
    color: var(--composer-ai, #6b36ce);
    background: var(--composer-ai-wash, #f7f3fe);
    border-color: var(--composer-ai, #6b36ce);

    &:hover,
    &:focus-visible {
        background: var(--composer-ai-wash-hover, #f0e8fd);
        border-color: var(--composer-ai, #6b36ce);
    }

    ${CardIcon} {
        color: inherit;
        background: none;

        ${Icon} {
            width: 22px;
            height: 22px;
        }
    }

    ${CardText} span {
        color: var(--composer-muted, #5c6b7a);
        font-size: 12px;
    }
`;

// COSTRUIRE DA SÉ È IL GESTO PIÙ FREQUENTE DOPO IL PRIMO: partiti da un modello o da una
// generazione, si torna qui per aggiungere un attributo o un gruppo. Bottoni con una riga sola di
// spiegazione e il loro tasto — la frase intera resta nel tooltip per chi la cerca.
const BuildButtons = styled.div<{ $columns: number }>`
    display: grid;
    grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
    gap: 8px;

    @media (max-width: 760px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    > * {
        min-width: 0;
    }
`;

const BuildButton = styled.button`
    display: flex;
    width: 100%;
    min-width: 0;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    color: var(--composer-fg, #1e2a38);
    background: var(--composer-panel, #fff);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 8px;
    cursor: pointer;
    text-align: left;

    &:hover,
    &:focus-visible {
        background: var(--composer-hover, #e9eff5);
        border-color: var(--composer-faint, #677078);
        outline: none;
    }

    > ${Icon} {
        width: 18px;
        height: 18px;
        flex: 0 0 18px;
        color: var(--composer-muted, #5c6b7a);
    }
`;

const BuildText = styled.span`
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 1px;

    strong,
    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    strong {
        font-size: 12.5px;
        font-weight: 600;
        line-height: 16px;
    }

    span {
        color: var(--composer-faint, #677078);
        font-size: 11px;
        line-height: 15px;
    }
`;

const ShortcutKey = styled.kbd`
    display: inline-flex;
    min-width: 18px;
    height: 18px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    padding: 0 4px;
    color: var(--composer-faint, #677078);
    background: var(--composer-sunken, #f4f7fa);
    border: 1px solid var(--composer-line, #dce3eb);
    border-radius: 4px;
    font-family: inherit;
    font-size: 10.5px;
    font-weight: 600;
`;

const getUseCases = (): GalleryCard[] => [
    {
        key: 'color',
        name: T._('Colours & finishes', 'Admin'),
        description: T._('Sell the same product in more than one colour or material', 'Admin'),
        icon: <PaletteIcon />
    },
    {
        key: 'showhide',
        name: T._('Parts on or off', 'Admin'),
        description: T._('Build versions by bringing pieces of the model in and out of view', 'Admin'),
        icon: <PartsIcon />
    },
    {
        key: 'engraving',
        name: T._('Personalised text', 'Admin'),
        description: T._('Let the buyer write something that ends up printed on the product', 'Admin'),
        icon: <TextIcon />
    },
    {
        key: 'image',
        name: T._("Buyer's own image", 'Admin'),
        description: T._('Let the buyer upload a picture and see it on the product', 'Admin'),
        icon: <ImageIcon />
    },
    {
        key: 'state',
        name: T._('Positions & states', 'Admin'),
        description: T._('Show the product open, closed, folded — one shape per choice', 'Admin'),
        icon: <StatesIcon />
    },
    {
        key: 'compound',
        name: T._('Two changes in one', 'Admin'),
        description: T._('A single choice that moves more than one thing at the same time', 'Admin'),
        icon: <CompoundIcon />
    }
];

const getBuildCards = (): BuildCard[] => [
    {
        key: 'attribute',
        name: T._('Empty attribute', 'Admin'),
        hint: T._('A choice you name and fill', 'Admin'),
        description: T._('A choice with no options yet and no effect on the model — you name it and fill it', 'Admin'),
        icon: <AttributeIcon />,
        shortcut: 'A'
    },
    {
        key: 'personalisation',
        name: T._('Empty personalisation', 'Admin'),
        hint: T._('A 2D area ready to configure', 'Admin'),
        description: T._('A 2D personalisation with an empty customizable area, ready to configure', 'Admin'),
        icon: <ImageIcon />,
        shortcut: 'P'
    },
    {
        key: 'group',
        name: T._('Empty group', 'Admin'),
        hint: T._('Attributes under one heading', 'Admin'),
        description: T._('A folder that gathers attributes under one heading', 'Admin'),
        icon: <GroupIcon />,
        shortcut: 'G'
    },
    {
        key: 'step',
        name: T._('New step', 'Admin'),
        hint: T._('A stage the buyer goes through', 'Admin'),
        description: T._('A stage the buyer goes through, with the attributes it asks for', 'Admin'),
        icon: <StairsIcon />,
        shortcut: 'S'
    }
];

// PORTARE DENTRO NON È COSTRUIRE DA VUOTO: copiare una struttura o prendere configurazioni pronte
// arriva con del contenuto, e sta su una riga sua per non confondersi con i quattro vuoti.
const getImportCards = (): BuildCard[] => [
    {
        key: 'import',
        name: T._('Copy from another product', 'Admin'),
        hint: T._('Reuse a structure built elsewhere', 'Admin'),
        description: T._('Bring over a structure you have already built somewhere else', 'Admin'),
        icon: <ImportIcon />
    },
    {
        key: 'preset',
        name: T._('Ready-made configurations', 'Admin'),
        hint: T._('Whole set-ups taken in one go', 'Admin'),
        description: T._('Whole set-ups the buyer takes in one go', 'Admin'),
        icon: <PresetIcon />
    }
];

const BuildCardButton: FC<{ card: BuildCard; onClick: () => void }> = ({ card, onClick }) => (
    <ZTooltip title={card.description} position='top'>
        <BuildButton type='button' aria-label={`${card.name}: ${card.description}`} aria-keyshortcuts={card.shortcut} onClick={onClick}>
            <Icon>{card.icon}</Icon>
            <BuildText>
                <strong>{card.name}</strong>
                <span>{card.hint}</span>
            </BuildText>
            {card.shortcut && <ShortcutKey aria-hidden='true'>{card.shortcut}</ShortcutKey>}
        </BuildButton>
    </ZTooltip>
);

const isAddedByBuyer = (card: GalleryCard) => card.key === 'engraving' || card.key === 'image';

const GalleryCardButton: FC<{ card: GalleryCard; path: TemplatePath; onClick: () => void }> = ({ card, path, onClick }) => (
    <Card type='button' $path={path} onClick={onClick}>
        <CardIcon $path={path}>
            <Icon>{card.icon}</Icon>
        </CardIcon>
        <CardText>
            <strong>{card.name}</strong>
            <span>{card.description}</span>
        </CardText>
    </Card>
);

interface ComposerAddGalleryDialogProps {
    onUseCase: (key: ComposerUseCaseKey, printingMethodID: string) => void;
    onBuild: (key: ComposerBuildCardKey) => void;
    onAI: () => void;
    onCancel: () => void;
}

const ComposerAddGalleryDialog: FC<ComposerAddGalleryDialogProps> = ({ onUseCase, onBuild, onAI, onCancel }) => {
    const data = useLazyLoadQuery<composerAddGalleryDialogPrintTypesQuery>(printTypesQuery, {}, { fetchPolicy: 'store-or-network' });
    const printingMethodID = String(data.printTypes?.edges?.[0]?.node?.printTypeID ?? '');
    const useCases = getUseCases();
    const buildCards = getBuildCards();

    // UN TASTO, UNA COSA VUOTA. Esc lo gestisce la finestra stessa, attraverso `onClose`. Solo lettere nude: con un modificatore il tasto è di qualcun altro, e
    // dentro un campo di testo la lettera è testo.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
            const target = event.target as HTMLElement | null;
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '') || target?.isContentEditable) return;

            const card = getBuildCards().find((item) => item.shortcut?.toLowerCase() === event.key.toLowerCase());
            if (!card) return;

            event.preventDefault();
            onBuild(card.key);
        };

        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onBuild]);

    return (
        <Dialog title={stringFormat(T._('Add to {0}', 'Admin'), productLogicName())} windowDecorator={GalleryWindow} onClose={onCancel}>
            <GalleryBody>
                <Lead>
                    {T._('Start from a template, let AI draft it, or build the structure yourself. Everything stays editable after creation.', 'Admin')}
                </Lead>
                <Group>
                    <GroupTitle>{T._('Start from a template', 'Admin')}</GroupTitle>
                    <TemplatePaths>
                        <PathPanel $path='chooses'>
                            <PathHeader $path='chooses'>
                                <strong>{T._('What the buyer chooses', 'Admin')}</strong>
                                <span>{T._('Options that change the product', 'Admin')}</span>
                            </PathHeader>
                            <PathCards $columns={2}>
                                {useCases
                                    .filter((card) => !isAddedByBuyer(card))
                                    .map((card) => (
                                        <GalleryCardButton
                                            key={card.key}
                                            card={card}
                                            path='chooses'
                                            onClick={() => onUseCase(card.key as ComposerUseCaseKey, printingMethodID)}
                                        />
                                    ))}
                            </PathCards>
                        </PathPanel>
                        <PathPanel $path='adds'>
                            <PathHeader $path='adds'>
                                <strong>{T._('What the buyer adds', 'Admin')}</strong>
                                <span>{T._('Their own content', 'Admin')}</span>
                            </PathHeader>
                            <PathCards $columns={1}>
                                {useCases.filter(isAddedByBuyer).map((card) => (
                                    <GalleryCardButton
                                        key={card.key}
                                        card={card}
                                        path='adds'
                                        onClick={() => onUseCase(card.key as ComposerUseCaseKey, printingMethodID)}
                                    />
                                ))}
                            </PathCards>
                        </PathPanel>
                    </TemplatePaths>
                </Group>
                <Group>
                    <GroupTitle>{T._('Or describe it and let AI build it', 'Admin')}</GroupTitle>
                    <AIBand type='button' onClick={onAI}>
                        <CardIcon>
                            <Icon>
                                <SparklesIcon />
                            </Icon>
                        </CardIcon>
                        <CardText>
                            <strong>{T._('Generate with AI', 'Admin')}</strong>
                            <span>{T._('Describe the product in your own words and get attributes and options, ready to edit', 'Admin')}</span>
                        </CardText>
                    </AIBand>
                </Group>
                <Group>
                    <GroupTitle>{T._('Or build the structure yourself', 'Admin')}</GroupTitle>
                    <BuildButtons $columns={4}>
                        {buildCards.map((card) => (
                            <BuildCardButton key={card.key} card={card} onClick={() => onBuild(card.key)} />
                        ))}
                    </BuildButtons>
                </Group>
                <Group>
                    <GroupTitle>{T._('Or import', 'Admin')}</GroupTitle>
                    <BuildButtons $columns={2}>
                        {getImportCards().map((card) => (
                            <BuildCardButton key={card.key} card={card} onClick={() => onBuild(card.key)} />
                        ))}
                    </BuildButtons>
                </Group>
            </GalleryBody>
        </Dialog>
    );
};

export default ComposerAddGalleryDialog;
